import { useTranslation } from 'react-i18next';
import { Card, Grid, SxProps, Typography } from '@mui/material';
import { getBlueprintCalculations } from 'features/blueprints';
import { Blueprint } from 'types';
import { DATE_FORMAT } from 'utils/constants';
import { representDate } from 'utils/formatters';
import { CostsTable } from './CostsTable';
import { EnergySourcesTable } from './EnergySourcesTable';
import { EnvironmentalTable } from './EnvironmentalTable';
import { generateTableData } from './tableData';
import { mapCalculationToTableData } from './utils';

type BusinessCaseComparisonTableProps = {
  blueprintDetails: Blueprint;
  sx?: SxProps;
  showProjectDates?: boolean;
};

export const BusinessCaseComparisonTable = ({
  blueprintDetails,
  sx,
  showProjectDates = false,
}: BusinessCaseComparisonTableProps) => {
  const { t } = useTranslation();
  const {
    conventional: baseCalculation,
    hybrid: hybridCalculation,
    hasGrid,
    hasKite,
  } = getBlueprintCalculations(blueprintDetails);
  const hasEnergySources = hasGrid || hasKite;

  if (!baseCalculation || !hybridCalculation) {
    return null;
  }

  // Transform data to match tableData expectations
  const baseData = mapCalculationToTableData(baseCalculation, { batteryRentalCostCents: 0 });
  const hybridData = mapCalculationToTableData(hybridCalculation);

  const areCostsCalculatedForWholeProject = baseCalculation.calculationResult.calculationPeriod === 'project';
  const isHybridCheaper =
    hybridCalculation.calculationResult.totalCostEstimateCents <
    baseCalculation.calculationResult.totalCostEstimateCents;

  const {
    costs: costsData,
    environmental: environmentalData,
    energySources: energySourcesData,
  } = generateTableData({
    t,
    hasGrid,
    hasKite,
    baseCalculation: baseData,
    hybridCalculation: hybridData,
    isEndDateProvided: areCostsCalculatedForWholeProject,
  });

  const COL_WIDTHS = [30, 20, 20, 20, 10];

  return (
    <Card sx={{ p: 4, pb: 2, borderRadius: '8px', ...sx }}>
      <Typography variant="h3">{t('blueprints:details.table.title')}</Typography>
      {showProjectDates && (
        <Typography variant="caption">
          {blueprintDetails.tsEnd
            ? `${representDate(blueprintDetails.tsStart, DATE_FORMAT)} / ${representDate(blueprintDetails.tsEnd, DATE_FORMAT)}`
            : t('blueprints:details.weeklyBreakdown')}
        </Typography>
      )}

      <Grid container spacing={1.5}>
        <Grid item xs={12} sx={{ order: isHybridCheaper ? 1 : 2 }}>
          <CostsTable
            data={costsData}
            hasSavingsHighlighted
            hasTotalSavingsHighlighted={isHybridCheaper}
            colWidths={COL_WIDTHS}
          />
        </Grid>

        <Grid item xs={12} sx={{ order: isHybridCheaper ? 2 : 1 }}>
          <EnvironmentalTable
            data={environmentalData}
            hasSavingsHighlighted={!isHybridCheaper}
            colWidths={COL_WIDTHS}
          />
        </Grid>

        {hasEnergySources && (
          <Grid item xs={12} sx={{ order: 3 }}>
            <EnergySourcesTable data={energySourcesData} colWidths={COL_WIDTHS} />
          </Grid>
        )}
      </Grid>
    </Card>
  );
};
